import useAuth from '@hooks/use-auth';
import { apiRequest } from '@services/fetcher';
import { Button, Card, Col, message, Row, Statistic } from 'antd';
import type { FC } from 'react';
import { Suspense, useState } from 'react';
import { Await, useLoaderData } from 'react-router-dom';

const ReportManagement: FC = () => {
  const { summary } = useLoaderData() as { summary: Promise<{ requests: number }> };
  const auth = useAuth();
  const [isRequestsReportDownloading, setRequestsReportDownloading] = useState<boolean>(false);

  const downloadReport = async () => {
    setRequestsReportDownloading(true);
    let url: string | undefined;
    let link: HTMLAnchorElement | undefined;

    try {
      const response = await apiRequest<Response>(`${import.meta.env.VITE_API_URL}/manage/organization/requests`, auth);

      if (response instanceof Error || !response.ok) {
        throw new Error('Download failed.');
      }

      const blob = await response.blob();
      url = window.URL.createObjectURL(blob);
      const contentDisposition = response.headers.get('content-disposition') ?? '';
      const filenameMatch = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(contentDisposition);
      const filename = filenameMatch?.[1] ? decodeURIComponent(filenameMatch[1]) : 'report.xlsx';

      // Create temporary link and trigger download
      link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
    } catch (error) {
      message.error(error instanceof Error ? error.message : 'Download failed.');
    } finally {
      link?.parentNode?.removeChild(link);
      if (url !== undefined) {
        window.URL.revokeObjectURL(url);
      }
      setRequestsReportDownloading(false);
    }
  };

  return (
    <Row gutter={16}>
      <Col span={12}>
        <Card variant="borderless">
          <Suspense fallback={<Statistic title="Monthly Requests" loading={true} valueStyle={{ color: '#3f8600' }} />}>
            <Await resolve={summary} errorElement={<p>Error loading reports summary</p>}>
              {(summary: { requests: number }) => (
                <Statistic title="Monthly Requests" value={summary.requests} valueStyle={{ color: '#3f8600' }} />
              )}
            </Await>
          </Suspense>
          <Button
            style={{ marginTop: 16 }}
            type="primary"
            onClick={downloadReport}
            loading={isRequestsReportDownloading}
          >
            Download
          </Button>
        </Card>
      </Col>
    </Row>
  );
};

export default ReportManagement;
